import type {
  ChatCompletionAssistantMessageParam,
  ChatCompletionContentPart,
  ChatCompletionMessageParam,
  ChatCompletionTool,
  ChatCompletionToolChoiceOption,
} from 'openai/resources/chat/completions';
import { UserError } from '../../errors';
import { type ModelRequest, type SerializedTool } from '../../model';
import * as protocol from '../../types/protocol';
import { getProviderDataWithoutReservedKeys } from './utils/provider-data';

const CHAT_COMPLETIONS_FUNCTION_NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

export function convertToolChoice(
  toolChoice: 'auto' | 'required' | 'none' | (string & {}) | undefined | null,
): ChatCompletionToolChoiceOption | undefined {
  if (toolChoice === undefined || toolChoice === null) return undefined;
  if (toolChoice === 'auto') return 'auto';
  if (toolChoice === 'required') return 'required';
  if (toolChoice === 'none') return 'none';
  return {
    type: 'function',
    function: { name: toolChoice },
  };
}

export function getCompatibleToolChoice(
  toolChoice: 'auto' | 'required' | 'none' | (string & {}) | undefined | null,
  tools: ChatCompletionTool[],
): ChatCompletionToolChoiceOption | undefined {
  const converted = convertToolChoice(toolChoice);
  if (converted === undefined || converted === 'auto' || converted === 'none') {
    return converted;
  }

  if (converted === 'required') {
    if (tools.length === 0) {
      throw new UserError(
        'modelSettings.toolChoice="required" requires at least one available tool in Chat Completions mode.',
      );
    }
    return converted;
  }

  if (typeof converted !== 'object' || !('function' in converted)) {
    return converted;
  }

  const availableToolNames = new Set(
    tools
      .map((tool) =>
        tool.type === 'function' && 'function' in tool
          ? tool.function.name
          : undefined,
      )
      .filter((name): name is string => typeof name === 'string'),
  );
  if (!availableToolNames.has(converted.function.name)) {
    throw new UserError(
      `modelSettings.toolChoice="${converted.function.name}" does not match any available tool or handoff in Chat Completions mode.`,
    );
  }

  return converted;
}

export function toolToOpenAI(tool: SerializedTool): ChatCompletionTool {
  if (tool.type === 'function') {
    return {
      type: 'function',
      function: {
        name: tool.name,
        description: tool.description || '',
        parameters: tool.parameters,
        strict: tool.strict,
      },
    };
  }
  throw new Error(
    `Hosted tools are not supported with the ChatCompletions API. Got tool type: ${
      tool.type
    }, tool: ${JSON.stringify(tool)}`,
  );
}

export function itemsToMessages(
  items: ModelRequest['input'],
): ChatCompletionMessageParam[] {
  if (typeof items === 'string') {
    return [{ role: 'user', content: items }];
  }
  const result: ChatCompletionMessageParam[] = [];
  let currentAssistantMsg: ChatCompletionAssistantMessageParam | null = null;
  let activeToolBatch: ToolBatchProjection | null = null;
  const flushSyntheticToolImages = () => {
    if (!activeToolBatch || activeToolBatch.imageContent.length === 0) {
      if (activeToolBatch?.remainingToolCallIds.size === 0) {
        activeToolBatch = null;
      }
      return;
    }
    if (activeToolBatch.remainingToolCallIds.size > 0) {
      return;
    }
    result.push({
      role: 'user',
      content: [
        {
          type: 'text',
          text: 'Images returned by tools in the previous tool batch.',
        },
        ...activeToolBatch.imageContent,
      ],
    });
    activeToolBatch = null;
  };
  const flushAssistantMessage = () => {
    if (currentAssistantMsg) {
      if (
        !currentAssistantMsg.tool_calls ||
        currentAssistantMsg.tool_calls.length === 0
      ) {
        delete currentAssistantMsg.tool_calls;
      }
      result.push(currentAssistantMsg);
      if (currentAssistantMsg.tool_calls?.length) {
        activeToolBatch = {
          remainingToolCallIds: new Set(
            currentAssistantMsg.tool_calls.map((call) => call.id),
          ),
          imageContent: [],
        };
      }
      currentAssistantMsg = null;
    }
  };
  const ensureAssistantMessage = () => {
    if (!currentAssistantMsg) {
      currentAssistantMsg = {
        role: 'assistant',
        content: null,
        tool_calls: [],
      };
    }
    return currentAssistantMsg;
  };
  for (const item of items) {
    if ((item as { type?: unknown }).type === 'compaction') {
      throw new UserError(
        'Responses compaction items are not supported for chat completions. Use Runner-level local compaction instead.',
      );
    }
    if (isMessageItem(item)) {
      const { content, role, providerData } = item;
      flushAssistantMessage();
      flushSyntheticToolImages();
      if (role === 'assistant') {
        const assistant: ChatCompletionAssistantMessageParam = {
          role: 'assistant',
          content: extractAllAssistantContent(content),
          ...getProviderDataWithoutReservedKeys(providerData, [
            'role',
            'content',
            'tool_calls',
            'audio',
          ]),
        };

        if (Array.isArray(content)) {
          // We have no audio support yet
          // const audio = content.find((c) => c.type === "audio");
          // if (audio) {
          //   assistant.audio = {
          //     id: "", // setting this to empty ID and expecting that the user sets providerData.id
          //     ...audio.providerData,
          //   };
          // }
        }

        result.push(assistant);
      } else if (role === 'user') {
        result.push({
          role,
          content: extractAllUserContent(content),
          ...getProviderDataWithoutReservedKeys(providerData, [
            'role',
            'content',
          ]),
        });
      } else if (role === 'system') {
        result.push({
          role: 'system',
          content: content,
          ...getProviderDataWithoutReservedKeys(providerData, [
            'role',
            'content',
          ]),
        });
      }
    } else if (item.type === 'reasoning') {
      flushSyntheticToolImages();
      const asst = ensureAssistantMessage();
      // @ts-expect-error - reasoning is not supported in every Chat Completion API spec
      // this is handling third party providers that support reasoning
      asst.reasoning = item.rawContent?.[0]?.text;
    } else if (item.type === 'hosted_tool_call') {
      flushSyntheticToolImages();
      if (item.name === 'file_search_call') {
        const asst = ensureAssistantMessage();
        const toolCalls = asst.tool_calls ?? [];
        const fileSearch = item;
        const { function: functionData, ...rest } =
          fileSearch.providerData ?? {};
        const { arguments: argumentData, ...remainingFunctionData } =
          functionData ?? {};

        toolCalls.push({
          id: fileSearch.id || '',
          type: 'function',
          function: {
            name: 'file_search_call',
            arguments: JSON.stringify({
              queries: fileSearch.providerData?.queries ?? [],
              status: fileSearch.status,
              ...getProviderDataWithoutReservedKeys(argumentData, [
                'queries',
                'status',
              ]),
            }),
            ...getProviderDataWithoutReservedKeys(remainingFunctionData, [
              'name',
              'arguments',
            ]),
          },
          ...getProviderDataWithoutReservedKeys(rest, [
            'id',
            'type',
            'function',
            'reasoning_content',
          ]),
        });
        asst.tool_calls = toolCalls;
      } else {
        throw new UserError(
          'Hosted tool calls are not supported for chat completions. Got item: ' +
            JSON.stringify(item),
        );
      }
    } else if (item.type === 'function_call') {
      flushSyntheticToolImages();
      const hasQualifiedOrInvalidName =
        typeof item.name === 'string' &&
        !CHAT_COMPLETIONS_FUNCTION_NAME_PATTERN.test(item.name);
      if (
        hasQualifiedOrInvalidName ||
        (typeof item.namespace === 'string' && item.namespace.trim().length > 0)
      ) {
        throw new UserError(
          'Namespaced function call history is not supported for chat completions. Please use the Responses API when replaying namespaced tool calls.',
        );
      }
      const asst = ensureAssistantMessage();
      const toolCalls = asst.tool_calls ?? [];
      const funcCall = item;
      const toolCallProviderData = getProviderDataWithoutReservedKeys(
        funcCall.providerData,
        [
          'id',
          'type',
          'function',
          'role',
          'content',
          'tool_calls',
          'audio',
          'reasoning_content',
        ],
      );
      const functionProviderData = getProviderDataWithoutReservedKeys(
        funcCall.providerData?.function,
        ['name', 'arguments', 'reasoning_content'],
      );
      toolCalls.push({
        id: funcCall.callId,
        type: 'function',
        function: {
          name: funcCall.name,
          arguments: funcCall.arguments ?? '{}',
          ...functionProviderData,
        },
        ...toolCallProviderData,
      });
      asst.tool_calls = toolCalls;
      Object.assign(
        asst,
        getProviderDataWithoutReservedKeys(funcCall.providerData, [
          'role',
          'content',
          'tool_calls',
          'audio',
          'id',
          'type',
          'function',
        ]),
      );
    } else if (item.type === 'function_call_result') {
      flushAssistantMessage();
      const funcOutput = item;
      const projectedOutput = projectFunctionCallOutputForChat(
        funcOutput.output,
        funcOutput,
      );

      result.push({
        role: 'tool',
        tool_call_id: funcOutput.callId,
        content: projectedOutput.toolContent,
        ...getProviderDataWithoutReservedKeys(funcOutput.providerData, [
          'role',
          'tool_call_id',
          'content',
        ]),
      });
      const toolBatch = activeToolBatch as ToolBatchProjection | null;
      if (toolBatch) {
        toolBatch.remainingToolCallIds.delete(funcOutput.callId);
        toolBatch.imageContent.push(...projectedOutput.imageContent);
        activeToolBatch = toolBatch;
        flushSyntheticToolImages();
      } else if (projectedOutput.imageContent.length > 0) {
        result.push({
          role: 'user',
          content: [
            {
              type: 'text',
              text: 'Images returned by tools in the previous tool result.',
            },
            ...projectedOutput.imageContent,
          ],
        });
      }
    } else if (item.type === 'unknown') {
      flushSyntheticToolImages();
      result.push({
        ...item.providerData,
      } as any);
    } else {
      const exhaustive = item satisfies never; // ensures that the type is exhaustive
      throw new Error(`Unknown item type: ${JSON.stringify(exhaustive)}`);
    }
  }
  flushAssistantMessage();
  flushSyntheticToolImages();
  return result;
}

type ToolBatchProjection = {
  remainingToolCallIds: Set<string>;
  imageContent: ChatCompletionContentPart[];
};

function isMessageItem(item: protocol.ModelItem): item is protocol.MessageItem {
  if (item.type === 'message') {
    return true;
  }

  if (typeof item.type === 'undefined' && typeof item.role === 'string') {
    return true;
  }

  return false;
}

export function extractAllAssistantContent(
  content: protocol.AssistantMessageItem['content'],
): string | ChatCompletionAssistantMessageParam['content'] {
  if (typeof content === 'string') {
    return content;
  }
  const out: ChatCompletionAssistantMessageParam['content'] = [];
  for (const c of content) {
    if (c.type === 'output_text') {
      out.push({
        type: 'text',
        text: c.text,
        ...getProviderDataWithoutReservedKeys(c.providerData, ['type', 'text']),
      });
    } else if (c.type === 'refusal') {
      out.push({
        type: 'refusal',
        refusal: c.refusal,
        ...getProviderDataWithoutReservedKeys(c.providerData, [
          'type',
          'refusal',
        ]),
      });
    } else if (c.type === 'image') {
    } else {
      const exhaustive = c satisfies never; // ensures that the type is exhaustive
      throw new Error(`Unknown content: ${JSON.stringify(exhaustive)}`);
    }
  }
  return out;
}

export function extractAllUserContent(
  content: protocol.UserMessageItem['content'],
): string | ChatCompletionContentPart[] {
  if (typeof content === 'string') {
    return content;
  }

  const out: ChatCompletionContentPart[] = [];
  for (const c of content) {
    if (c.type === 'input_text') {
      out.push({
        type: 'text',
        text: c.text,
        ...getProviderDataWithoutReservedKeys(c.providerData, ['type', 'text']),
      });
    } else if (c.type === 'input_image') {
      // The Chat Completions API only accepts image URLs. If we see a file reference we reject it
      // early so callers get an actionable error instead of a cryptic API response.
      const imageSource =
        typeof c.image === 'string'
          ? c.image
          : typeof (c as any).imageUrl === 'string'
            ? (c as any).imageUrl
            : undefined;

      if (!imageSource) {
        throw new Error(
          `Only image URLs are supported for input_image: ${JSON.stringify(c)}`,
        );
      }
      const rest = getProviderDataWithoutReservedKeys(c.providerData, [
        'type',
        'image_url',
      ]);
      const imageUrl = getProviderDataWithoutReservedKeys(
        c.providerData?.image_url,
        ['url'],
      );
      out.push({
        type: 'image_url',
        image_url: {
          url: imageSource,
          ...imageUrl,
        },
        ...rest,
      });
      /*} else if (c.type === "input_file") {
      // Chat Completions API supports file inputs via the "file" content part type.
      // See: https://platform.openai.com/docs/guides/pdf-files?api-mode=chat
      const file: ChatCompletionContentPart.File["file"] = {};

      if (typeof c.file === "string") {
        const value = c.file.trim();
        if (value.startsWith("data:")) {
          file.file_data = value;
        } else {
          throw new UserError(
            `Chat Completions only supports data URLs for file input. If you're trying to pass an uploaded file's ID, use an object with the id property instead: ${JSON.stringify(c)}`,
          );
        }
      } else if (c.file && typeof c.file === "object" && "id" in c.file) {
        file.file_id = (c.file as { id: string }).id;
      } else {
        throw new UserError(
          `File input requires a data URL or file ID: ${JSON.stringify(c)}`,
        );
      }

      // Handle filename from the content item or providerData
      if (c.filename) {
        file.filename = c.filename;
      } else if (c.providerData?.filename) {
        file.filename = c.providerData.filename;
      }

      const rest = getProviderDataWithoutReservedKeys(c.providerData, [
        "type",
        "file",
        "filename",
      ]);
      out.push({
        type: "file",
        file,
        ...rest,
      });

    } else if (c.type === "audio") {
      if (typeof c.audio !== "string") {
        throw new UserError(
          `Chat Completions only supports inline audio data for input_audio: ${JSON.stringify(c)}`,
        );
      }
      const inputAudioFormat =
        c.format === "wav" || c.format === "mp3"
          ? c.format
          : c.providerData?.input_audio?.format === "wav" ||
              c.providerData?.input_audio?.format === "mp3"
            ? c.providerData.input_audio.format
            : undefined;
      if (!inputAudioFormat) {
        throw new UserError(
          `Chat Completions input_audio requires format "wav" or "mp3": ${JSON.stringify(c)}`,
        );
      }
      const rest = getProviderDataWithoutReservedKeys(c.providerData, [
        "type",
        "input_audio",
      ]);
      const inputAudio = getProviderDataWithoutReservedKeys(
        c.providerData?.input_audio,
        ["data", "format"],
      );
      out.push({
        type: "input_audio",
        input_audio: {
          data: c.audio,
          format: inputAudioFormat,
          ...inputAudio,
        } as ChatCompletionContentPartInputAudio["input_audio"],
        ...rest,
      });
    */
    } else if (c.type === 'input_file') {
      // Upload references are projected to text upstream (see
      // runner/attachment-flattening); inline file inputs aren't wired for the
      // Chat Completions converter, so reaching here is unsupported.
      throw new UserError(
        `Chat Completions does not support input_file content: ${JSON.stringify(c)}`,
      );
    } else {
      const exhaustive = c satisfies never; // ensures that the type is exhaustive
      throw new Error(`Unknown content: ${JSON.stringify(exhaustive)}`);
    }
  }
  return out;
}

function projectFunctionCallOutputForChat(
  output: protocol.FunctionCallResultItem['output'],
  item: protocol.FunctionCallResultItem,
): { toolContent: string; imageContent: ChatCompletionContentPart[] } {
  if (typeof output === 'string') {
    return { toolContent: output, imageContent: [] };
  }

  if (Array.isArray(output)) {
    const toolText: string[] = [];
    const imageContent: ChatCompletionContentPart[] = [];
    for (const part of output) {
      if (part.type === 'input_text') {
        toolText.push(part.text);
        continue;
      }
      if (part.type === 'input_image') {
        const projection = projectInputImageToolOutputForChat(part, item);
        toolText.push(projection.summary);
        imageContent.push(...projection.content);
        continue;
      }
      throw new UserError(
        `Chat Completions does not support this tool output content: ${JSON.stringify(part)}`,
      );
    }
    return {
      toolContent: toolText.join('\n'),
      imageContent,
    };
  }

  if (
    isRecord(output) &&
    output.type === 'text' &&
    typeof output.text === 'string'
  ) {
    return { toolContent: output.text, imageContent: [] };
  }

  if (isRecord(output) && output.type === 'image') {
    const normalized = legacyImageOutputToInputImage(output);
    const projection = projectInputImageToolOutputForChat(normalized, item);
    return {
      toolContent: projection.summary,
      imageContent: projection.content,
    };
  }

  throw new UserError(
    'Only text and image tool outputs are supported for chat completions. Got item: ' +
      JSON.stringify(output),
  );
}

function projectInputImageToolOutputForChat(
  part: protocol.InputImage,
  item: protocol.FunctionCallResultItem,
): { summary: string; content: ChatCompletionContentPart[] } {
  if (typeof part.image !== 'string' || part.image.trim().length === 0) {
    throw new UserError(
      `Chat Completions image tool outputs require inline data URLs or URLs: ${JSON.stringify(part)}`,
    );
  }
  const metadata = getViewImageMetadata(part.providerData);
  const label = metadata
    ? `${item.callId}: ${metadata.path} ${metadata.mimeType}${metadata.width && metadata.height ? ` ${metadata.width}x${metadata.height}` : ''}`
    : `${item.callId}: image returned by ${item.name}`;
  const summary = metadata
    ? `[image returned by ${item.name}: ${metadata.path} ${metadata.mimeType}${metadata.width && metadata.height ? ` ${metadata.width}x${metadata.height}` : ''}${metadata.byteSize ? ` ${formatByteSize(metadata.byteSize)}` : ''}]`
    : `[image returned by ${item.name}]`;
  return {
    summary,
    content: [
      { type: 'text', text: label },
      {
        type: 'image_url',
        image_url: {
          url: part.image.trim(),
          ...getOpenAIImageUrlOptions(part),
        },
      },
    ],
  };
}

function legacyImageOutputToInputImage(
  output: Record<string, unknown>,
): protocol.InputImage {
  const image = output.image;
  if (typeof image === 'string') {
    return {
      type: 'input_image',
      image,
      providerData: isRecord(output.providerData)
        ? output.providerData
        : undefined,
    };
  }
  if (isRecord(image)) {
    const providerData = isRecord(output.providerData)
      ? output.providerData
      : undefined;
    if (typeof image.fileId === 'string') {
      return {
        type: 'input_image',
        image: { id: image.fileId },
        providerData,
      };
    }
    if (typeof image.url === 'string') {
      return {
        type: 'input_image',
        image: image.url,
        providerData,
      };
    }
    if (typeof image.data === 'string') {
      const mediaType =
        typeof image.mediaType === 'string' ? image.mediaType : 'image/jpeg';
      return {
        type: 'input_image',
        image: image.data.startsWith('data:')
          ? image.data
          : `data:${mediaType};base64,${image.data}`,
        providerData,
      };
    }
  }
  throw new UserError(
    `Chat Completions image tool outputs require string image data, data objects, URLs, or file ids: ${JSON.stringify(output)}`,
  );
}

function getOpenAIImageUrlOptions(
  part: protocol.InputImage,
): Record<string, unknown> {
  const imageUrlProviderData = isRecord(part.providerData?.image_url)
    ? getProviderDataWithoutReservedKeys(part.providerData.image_url, ['url'])
    : {};
  const openaiProviderData = isRecord(part.providerData?.openai)
    ? part.providerData.openai
    : {};
  const detail =
    typeof openaiProviderData.detail === 'string'
      ? openaiProviderData.detail
      : typeof part.detail === 'string'
        ? part.detail
        : undefined;
  return {
    ...imageUrlProviderData,
    ...(detail ? { detail } : {}),
  };
}

function getViewImageMetadata(value: unknown): {
  path: string;
  mimeType: string;
  byteSize?: number;
  width?: number;
  height?: number;
} | null {
  if (!isRecord(value) || value.kind !== 'view_image') {
    return null;
  }
  if (typeof value.path !== 'string' || typeof value.mimeType !== 'string') {
    return null;
  }
  return {
    path: value.path,
    mimeType: value.mimeType,
    byteSize: typeof value.byteSize === 'number' ? value.byteSize : undefined,
    width: typeof value.width === 'number' ? value.width : undefined,
    height: typeof value.height === 'number' ? value.height : undefined,
  };
}

function formatByteSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes}B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)}KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null;
}
